import { useEffect, useState } from 'react'
import Icon from '../Icon'
import { browsePalms, createSpecies, deleteSpecies, listSpecies, type PalmMatch } from '../../lib/ppc'

/**
 * PalmTraits 1.0, as a table Dillon can read.
 *
 * Two thousand five hundred and fifty-seven palm species, measured and
 * published. The identifier uses this behind the scenes; this screen is for
 * when a person wants to look something up themselves — what does this get to,
 * does it sucker, is the trunk spiny, what does the fruit look like.
 *
 * ── Paged, not virtualised ────────────────────────────────────────────────────
 *
 * Fifty rows at a time with a "Show more" button. Dillon is on mobile signal
 * holding a plant; a screen that fetches 2,557 rows to show him twelve is the
 * wrong trade, and infinite scroll on a phone means never reaching the thing
 * at the bottom. A search box gets you there faster than any amount of
 * scrolling anyway.
 */
const PAGE = 50

/**
 * Height, in three states.
 *
 * NULL is unrecorded and shows a dash. ZERO is recorded and means no aerial
 * trunk — 116 species have none. "0 m" reads as a plant that does not grow;
 * a dash would throw away something the checklist actually knows.
 */
const height = (palm: PalmMatch) =>
  palm.heightM === null ? '—' : palm.heightM === 0 ? 'no trunk' : `${palm.heightM} m`

/** Two names are the same plant if the binomial matches, whatever the case. */
const key = (name: string | null | undefined) => (name ?? '').trim().toLowerCase()

export default function PalmReference(
  { onBack, canWrite }: { onBack: () => void; canWrite: boolean },
) {
  /*
   * Which of these Dillon already grows.
   *
   * Loaded once, held as a Set of scientific names, and updated locally the
   * moment something is added — a re-fetch of the whole species list after
   * every tap would be four round trips to add four palms, on nursery signal.
   */
  /*
   * Name → the species id, because undoing an add needs the id and the name is
   * all the checklist knows. A Map rather than a Set for exactly that reason.
   */
  const [mine, setMine] = useState<Map<string, string> | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  /** Which row is asking "remove?" — an add is one tap, an undo is two. */
  const [confirming, setConfirming] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    void listSpecies()
      .then((all) => setMine(new Map(
        all.filter((one) => one.scientific_name)
          .map((one) => [key(one.scientific_name), one.id] as const),
      )))
      .catch(() => setMine(new Map()))
  }, [])

  /**
   * Bring a palm into the library.
   *
   * It lands at 'pending', NOT confirmed. The checklist is a source for what
   * the species is like; it is not evidence that this nursery's plant is one.
   * Confirming still means a person standing in front of the plant, with a
   * source — that path is unchanged.
   *
   * The common name is the scientific name, because PalmTraits has no common
   * names. Dillon renames it to whatever he calls it on the bench, which is
   * why the species form now exists.
   */
  async function add(palm: PalmMatch) {
    setBusy(palm.scientificName)
    setProblem(null)
    try {
      const created = await createSpecies({
        commonName: palm.scientificName,
        scientificName: palm.scientificName,
        matureSize: palm.matureSize ?? undefined,
        description: palm.characteristics ?? undefined,
        idStatus: 'pending',
      })
      setMine((current) => new Map(current).set(key(palm.scientificName), created.id))
    } catch (failure) {
      setProblem(failure instanceof Error ? failure.message : 'That could not be added.')
    } finally {
      setBusy(null)
    }
  }

  /**
   * Undo the add.
   *
   * The server refuses if any plant points at the species — deleting would
   * detach them silently, because the foreign key is SET NULL. That refusal is
   * shown, not swallowed: "3 plants still point at this" is the answer to the
   * question Dillon is actually asking.
   */
  async function remove(palm: PalmMatch) {
    const id = mine?.get(key(palm.scientificName))
    if (!id) return
    setBusy(palm.scientificName)
    setProblem(null)
    try {
      await deleteSpecies(id)
      setMine((current) => {
        const next = new Map(current)
        next.delete(key(palm.scientificName))
        return next
      })
      setConfirming(null)
    } catch (failure) {
      setProblem(failure instanceof Error ? failure.message : 'That could not be removed.')
    } finally {
      setBusy(null)
    }
  }

  /**
   * Add, the mark that says it is already his, or the undo.
   *
   * Adding is one tap; removing is two. Not ceremony — these rows are 44px
   * apart on a phone held in one hand, and a mis-tap that silently deletes a
   * species profile is a worse outcome than a mis-tap that adds one.
   */
  function AddControl({ palm }: { palm: PalmMatch }) {
    if (!canWrite) return null
    const name = palm.scientificName
    const working = busy === name

    if (mine?.has(key(name))) {
      if (confirming === name) {
        return (
          <span className="row">
            <button type="button" className="removepalm" disabled={working} onClick={() => void remove(palm)}>
              {working ? 'Removing…' : 'Remove'}
            </button>
            <button type="button" className="link tap" onClick={() => setConfirming(null)}>Keep</button>
          </span>
        )
      }
      return (
        <button
          type="button" className="inlist"
          title="In your species list — tap to remove"
          onClick={() => { setProblem(null); setConfirming(name) }}
        >
          <Icon name="check" size={15} /> In your list
        </button>
      )
    }

    return (
      <button
        type="button" className="addpalm"
        disabled={working || mine === null}
        onClick={() => void add(palm)}
      >
        <Icon name="plus" size={15} />
        {working ? 'Adding…' : 'Add'}
      </button>
    )
  }

  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<'name' | 'height'>('name')
  const [dir, setDir] = useState<'asc' | 'desc'>('asc')
  const [palms, setPalms] = useState<PalmMatch[]>([])
  const [matched, setMatched] = useState(0)
  const [total, setTotal] = useState(0)
  const [source, setSource] = useState('')
  const [loading, setLoading] = useState(true)
  const [more, setMore] = useState(false)

  // Debounced, and it starts over whenever the query or the order changes —
  // appending a page of a different sort to a page of the old one shows rows
  // twice and hides others.
  useEffect(() => {
    let live = true
    setLoading(true)
    const timer = setTimeout(() => {
      void browsePalms({ q: query, sort, dir, limit: PAGE, offset: 0 })
        .then((page) => {
          if (!live) return
          setPalms(page.palms)
          setMatched(page.matched)
          setTotal(page.total)
          setSource(page.source)
        })
        .catch(() => { if (live) setPalms([]) })
        .finally(() => { if (live) setLoading(false) })
    }, 200)
    return () => { live = false; clearTimeout(timer) }
  }, [query, sort, dir])

  async function showMore() {
    setMore(true)
    try {
      const page = await browsePalms({ q: query, sort, dir, limit: PAGE, offset: palms.length })
      setPalms((current) => [...current, ...page.palms])
    } finally {
      setMore(false)
    }
  }

  /** Clicking the column you are already sorted by turns it around. */
  const orderBy = (next: 'name' | 'height') => {
    if (sort === next) setDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSort(next); setDir('asc') }
  }
  const arrow = (column: 'name' | 'height') =>
    sort === column ? (dir === 'asc' ? ' ↑' : ' ↓') : ''

  return (
    <>
      {/* Just "Back": this screen is reached from the collection AND from
          species, and it returns to whichever you came from. */}
      <button type="button" className="backlink" onClick={onBack} style={{ marginBottom: 10 }}>
        <Icon name="back" size={14} /> Back
      </button>

      <div className="pagehead">
        <div>
          <p className="eyebrow">Every palm in the world, measured</p>
          <h1>Palm reference.</h1>
          <p>
            {total ? total.toLocaleString() : '2,557'} species from a published checklist.
            Look up what something gets to before you price it.
          </p>
        </div>
      </div>

      <div className="searchrow">
        <span className="field">
          <Icon name="search" size={16} />
          <input
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name — try Roystonea…" aria-label="Search palms"
            autoCapitalize="off" autoCorrect="off" spellCheck={false}
          />
        </span>
      </div>

      {/*
        * Sorting lives HERE, above the results, not only in the table header.
        * The header is the first thing a phone loses: it is off-screen the
        * moment you scroll into the list, and on a narrow screen it was never
        * really readable in the first place. A control you cannot see is a
        * feature that does not exist.
        */}
      <div className="sortbar" role="group" aria-label="Sort palms">
        <span className="muted small">Sort</span>
        <button
          type="button" className={sort === 'name' ? 'is-on' : ''}
          aria-pressed={sort === 'name'} onClick={() => orderBy('name')}
        >
          Name{arrow('name')}
        </button>
        <button
          type="button" className={sort === 'height' ? 'is-on' : ''}
          aria-pressed={sort === 'height'} onClick={() => orderBy('height')}
        >
          Height{arrow('height')}
        </button>
      </div>

      {problem && <p className="error" role="status">{problem}</p>}

      <p className="muted small">
        {loading ? 'Looking…' : (
          query.trim()
            ? `${matched.toLocaleString()} match${matched === 1 ? '' : 'es'}`
            : `Showing ${palms.length.toLocaleString()} of ${matched.toLocaleString()}`
        )}
      </p>

      {!loading && palms.length === 0 && (
        <div className="card empty">
          <Icon name="book" size={30} />
          <h3>Nothing by that name.</h3>
          <p>The checklist covers palms only, under their scientific names. Try the genus on its own — <i>Roystonea</i>, <i>Dypsis</i>, <i>Cocos</i>.</p>
        </div>
      )}

      {palms.length > 0 && (
        /*
         * One set of markup, two shapes. `palm-table` turns the rows into
         * cards below 720px rather than leaving a 900px-wide table to be
         * dragged sideways with a thumb — see index.css. Each cell carries its
         * own label, so the card can name a value without the header row.
         */
        <>
          {/*
            * Two shapes, deliberately, rather than one set of markup bent by
            * CSS into a phone layout it was never designed for.
            *
            * The CSS version stacked six labelled cells down the card — six
            * lines to say what three say, most of each line empty to the right
            * of a value like "20 cm". That is the dead space. The card below
            * groups the facts the way a person reads them: what it is, how big
            * it gets, what it looks like.
            *
            * They sit next to each other on purpose. A new column is one line
            * in each, and the second line is impossible to miss while adding
            * the first.
            */}
          <div className="table-wrap plain wide-only">
            <table className="library palm-table">
              <thead>
                <tr>
                  <th>
                    <button type="button" onClick={() => orderBy('name')}>Species{arrow('name')}</button>
                  </th>
                  <th>
                    <button type="button" onClick={() => orderBy('height')}>Max height{arrow('height')}</button>
                  </th>
                  <th>Stem width</th>
                  <th>Habit</th>
                  <th>Layer</th>
                  <th>Fruit</th>
                  {canWrite && <th aria-label="Add to your species" />}
                </tr>
              </thead>
              <tbody>
                {palms.map((palm) => (
                  <tr key={palm.scientificName}>
                    <td className="title"><i>{palm.scientificName}</i></td>
                    {/*
                      * Three states, not two. NULL is unrecorded and shows a
                      * dash. ZERO is recorded, and means the palm has no aerial
                      * trunk — 116 of them do not. Rendering that as "0 m" reads
                      * as a plant that does not grow, and rendering it as "—"
                      * throws away something the checklist actually knows.
                      */}
                    <td>{height(palm)}</td>
                    <td>{palm.trunkCm === null ? '—' : `${palm.trunkCm} cm`}</td>
                    <td>{palm.habit.length ? palm.habit.join(', ') : '—'}</td>
                    <td>{palm.layer ?? '—'}</td>
                    <td>{palm.fruit ?? '—'}</td>
                    {canWrite && <td className="addcell"><AddControl palm={palm} /></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="palm-cards narrow-only">
            {palms.map((palm) => (
              <li key={palm.scientificName}>
                <div className="palm-card-head">
                  <h3><i>{palm.scientificName}</i></h3>
                  <AddControl palm={palm} />
                </div>
                {/* One line of facts, not three of mostly-empty ones. */}
                <p className="facts">
                  {[height(palm), palm.trunkCm === null ? null : `${palm.trunkCm} cm stem`, palm.layer]
                    .filter(Boolean).join(' · ')}
                </p>
                {palm.habit.length > 0 && <p className="habit">{palm.habit.join(', ')}</p>}
                {palm.fruit && <p className="muted small">Fruit — {palm.fruit}</p>}
              </li>
            ))}
          </ul>
        </>
      )}

      {palms.length < matched && (
        <div className="row" style={{ marginTop: 14 }}>
          <button type="button" onClick={() => void showMore()} disabled={more}>
            {more ? 'Loading…' : `Show more (${(matched - palms.length).toLocaleString()} left)`}
          </button>
        </div>
      )}

      {source && (
        <p className="muted small" style={{ marginTop: 18 }}>
          <Icon name="book" size={13} /> {source}. Public domain; heights are maximum
          recorded stem height, not what a plant will reach in a pot.
        </p>
      )}
    </>
  )
}
